import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimeRangePicker } from '@mui/x-date-pickers-pro/DateTimeRangePicker';
import { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import dayjs, { Dayjs } from 'dayjs';
import { frFR } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/fr';
import { FormControl } from '@mui/material';
import { BaseMUIComponentProps } from '../types/Types';

// FIXME: to be managed in the main app to sync with translations
dayjs.locale('fr');

const defaultPredefinedRanges: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
        label: "Cette semaine",
        getValue: () => {
            const today = dayjs();
            return [today.startOf('week'), today.endOf('week')];
        },
    },
    {
        label: "Dernière semaine",
        getValue: () => {
            const today = dayjs();
            const prevWeek = today.subtract(7, 'day');
            return [prevWeek.startOf('week'), prevWeek.endOf('week')];
        },
    },
    {
        label: "Derniers 7 jours",
        getValue: () => {
            const today = dayjs();
            return [today.subtract(7, 'day'), today];
        },
    },
    {
        label: "Ce mois-ci",
        getValue: () => {
            const today = dayjs();
            return [today.startOf('month'), today.endOf('month')];
        },
    },
    {
        label: "Prochain mois",
        getValue: () => {
            const today = dayjs();
            const startOfNextMonth = today.endOf('month').add(1, 'day');
            return [startOfNextMonth, startOfNextMonth.endOf('month')];
        },
    },
];

/**
 * Props for the DateTimeRangePicker component
 */
interface DateTimeRangePickerComponentProps extends BaseMUIComponentProps {
    defaultValues?: [Date, Date];
    predefinedRanges?: PickersShortcutsItem<DateRange<Dayjs>>[];
    format?: string;
    handleDatesChange: (newValue: [Date, Date] | null) => void;
    required?: boolean;
    error?: boolean;
    helperText?: string;
};

/**
 * MUI Date Time Range Picker Component
 * @param label Optionnal label for default display
 * @param defaultValues Optionnal defaultValues
 * @param predefinedRanges Optionnal predefinedRanges for shortcuts
 * @param format Optionnal date format
 * @param onChange Callback function to handle date changes
 * @param required Whether the date range is required
 * @param error Whether the date range has an error state
 * @param helperText Optional helper text to display below the input field
 * @returns 
 */
export function DateTimeRangePickerComponent({
    label = "Choisir une période",
    defaultValues,
    predefinedRanges,
    format = "DD/MM/YYYY HH:mm",
    handleDatesChange: onChange,
    required = true,
    error = false,
    helperText = '',
    ...props
}: DateTimeRangePickerComponentProps) {
    const safeDefault: [Date, Date] = defaultValues ?? [new Date(), new Date()];
    return (
        <FormControl fullWidth margin="normal">
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr" localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}>
                <DateTimeRangePicker
                    label={required ? `${label} *` : label}
                    format={format}
                    defaultValue={[dayjs(safeDefault[0]), dayjs(safeDefault[1])]}
                    onChange={(newValue) => {
                        if (newValue[0] && newValue[1]) {
                            onChange([newValue[0].toDate(), newValue[1].toDate()]);
                        }
                        else {
                            onChange(null);
                        }
                    }}
                    slotProps={{
                        shortcuts: {
                            items: predefinedRanges && predefinedRanges.length > 0
                                ? predefinedRanges
                                : defaultPredefinedRanges,
                        },
                        textField: {
                            error,
                            helperText: error ? helperText : '',
                        },
                        field: { clearable: true }
                    }}
                />
            </LocalizationProvider>
        </FormControl>
    );
}

